import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import React, { memo } from 'react';
import { FormattedMessage } from 'react-intl';

import { grey } from '../../styles/mui/colors';

// marketplaceDefaults.css strips list-style from every <ul>.
const refundLimitsListSx = { listStyleType: 'disc' };

/**
 * This component is used to show the cancellation policy of the listing.
 * @param {object} props - props of the component
 * @param {object} props.publicData - cancellation policy of the listing
 * @param {object} props.options - options for the cancellation policy
 * @returns {React.JSX.Element|null}
 */

const SectionCancellationPolicyMaybeComponent = ({ publicData, cancellationPolicyOptions }) => {
  const cancellationPolicyTier = publicData?.cancellationPolicyTier;
  const cancellationPolicy = publicData?.cancellationPolicy || {
    topLimit: 80,
    middleLimit: 50,
    bottomLimit: 30,
  };


  if (cancellationPolicyTier) {
    return (<Box bgcolor={grey.grey5} display="flex" justifyContent="center" width="100%">
      <Container maxWidth="xl" sx={{ marginTop: 8, marginBottom: 8 }}>
        <Accordion>
          <AccordionSummary
            aria-controls="cancellationPolicy-content"
            expandIcon={<ExpandMoreIcon />}
            id="cancellationPolicy-header"
          >
            <Typography variant="h4">
              <FormattedMessage id="ListingPage.cancellationPolicyTitle" />
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body1">
              <FormattedMessage
                id={`ListingPage.cancellationPolicy.${cancellationPolicyTier}`}
              />
            </Typography>
            <Typography mt={2} variant="body2">
              <FormattedMessage id="ListingPage.cancellationPolicy.info" />
            </Typography>
            <Typography mt={2} variant="body2">
              <FormattedMessage id="ListingPage.cancellationPolicy.serviceFee" />
            </Typography>
          </AccordionDetails>
        </Accordion>
      </Container>
    </Box>);
  } else {
    const findOptionLabel = (limitKey, defaultKey = '80') =>
      cancellationPolicyOptions[limitKey].options.find(
        (option) => option.key === cancellationPolicy[limitKey].toString(),
      )?.label || cancellationPolicyOptions[limitKey].options.find((option) => option.key === defaultKey)?.label;

    const topLimitLabel = typeof cancellationPolicy === 'object' ? findOptionLabel('topLimit') : '80';
    const middleLimitLabel = typeof cancellationPolicy === 'object' ? findOptionLabel('middleLimit') : '50';
    const bottomLimitLabel = typeof cancellationPolicy === 'object' ? findOptionLabel('bottomLimit') : '30';

    return (
      <Box bgcolor={grey.grey5} display="flex" justifyContent="center" width="100%">
        <Container maxWidth="xl" sx={{ marginTop: 8, marginBottom: 8 }}>
          <Accordion>
            <AccordionSummary
              aria-controls="cancellationPolicy-content"
              expandIcon={<ExpandMoreIcon />}
              id="cancellationPolicy-header"
            >
              <Typography variant="h4">
                <FormattedMessage id="ListingPage.cancellationPolicyTitle" />
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {/* A <ul>, not Typography's default <p>: an <li> inside a <p> is invalid HTML
                  that the browser re-parses, breaking hydration of the whole page. */}
              <Typography component="ul" sx={refundLimitsListSx} variant="body1">
                <FormattedMessage
                  id="ListingPage.cancellationPolicy.>72h"
                  tagName="li"
                  values={{ percent: topLimitLabel }}
                />
                <FormattedMessage
                  id="ListingPage.cancellationPolicy.72h-24h"
                  tagName="li"
                  values={{ percent: middleLimitLabel }}
                />
                <FormattedMessage
                  id="ListingPage.cancellationPolicy.<24h"
                  tagName="li"
                  values={{ percent: bottomLimitLabel }}
                />
              </Typography>
              <Typography mt={2} variant="body2">
                <FormattedMessage id="ListingPage.cancellationPolicy.info" />
              </Typography>
              <Typography mt={2} variant="body2">
                <FormattedMessage id="ListingPage.cancellationPolicy.serviceFee" />
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Container>
      </Box>
    );
  }
};
SectionCancellationPolicyMaybeComponent.displayName = 'SectionCancellationPolicyMaybe';

const SectionCancellationPolicyMaybe = memo(SectionCancellationPolicyMaybeComponent);

export default SectionCancellationPolicyMaybe;
